import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { LocationPill } from '../../components/LocationPill';
import { programLocationName } from '../../types/location';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CalendarOff, History, Search, X } from 'lucide-react';
import { StaffLayout } from '../StaffLayout';
import { AbsenceReasonModal } from '../components/AbsenceReasonModal';
import { CheckOutModal } from '../components/CheckOutModal';
import { GroupSwitcher } from '../components/GroupSwitcher';
import { ScrollTabs } from '../components/ScrollTabs';
import { SwipeableRow, type OpenSide, type SwipeAction } from '../components/SwipeableRow';
import { AttendanceRecordFacts } from '../../components/attendance/AttendanceRecordFacts';
import { AttendanceStatusBadge } from '../../components/attendance/AttendanceStatusBadge';
import {
  useAttendanceByPrograms,
  useUpsertRoster,
  useTeacherCheckIn,
  useMarkAbsent,
  useClearAbsence,
  useUndoParentCheckOut,
  useMarkPickedUp,
  useBulkCheckIn,
  useBulkClassCheckOut,
} from '../../hooks/useAttendance';
import { attendanceModeFor, type AttendanceMode } from '../../utils/attendanceMode';
import { useUrlTabState } from '../../hooks/useUrlTabState';
import { useStudentPhotoUrls } from '../../hooks/useStudentPhotoUrls';
import { StudentAvatar } from '../../components/StudentAvatar';
import { StudentPhotoLightbox } from '../../components/StudentPhotoLightbox';
import { AttendanceStatus, REAL_SESSION_KEY } from '../../types/attendance';
import {
  compareByFirstName,
  formatGradeLabel,
  gradeBand,
  gradeRank,
  groupBySchoolThenGrade,
  normalizeGrade,
  shortSchoolLabels,
  GRADE_BAND_LABEL,
  GRADE_BAND_ORDER,
  UNKNOWN_GRADE,
  type GradeBand,
} from '../../utils/rosterGrouping';
import { shortProgramLabel } from '../../utils/programLabel';
import { formatWeekdayDateDisplay } from '../../utils/date';
import { formatSessionTimeRange } from '../../utils/schedule';
import { buildRosterGroups, pickDefaultGroupId, resolveGroup } from '../rosterGroups';
import { groupsInSamePillar } from '../staffPillars';
import type { HistoryBand } from '../../hooks/useAttendanceHistory';
import {
  flexDayStatus,
  useStaffRosterEntries,
  useVirtualClassRoster,
  type RosterEntry,
} from '../useStaffRosterEntries';
import { useAfterSchoolProgramsOn, useTodaysPrograms } from '../useTodaysPrograms';
import { virtualClassKey, virtualGradeLabel, type VirtualGrade } from '../virtualClasses';
import { SampleModeBanner, SampleWatermark } from '../components/SampleModeBanner';
import { VoiceAttendance } from '../voice/VoiceAttendance';
import { AfterSchoolBadge, isEnrichmentRoster } from '../components/AfterSchoolBadge';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
  /** Virtual classes this teacher holds. `null` means none — they are an explicit grant. */
  virtual_class_keys?: string[] | null;
}

interface Props {
  staffUser: StaffUser;
}

function shortDate(dateStr: string): string {
  const [, mm, dd] = dateStr.split('-');
  return `${parseInt(mm)}/${parseInt(dd)}`;
}

function formatSessionDate(dateStr: string): string {
  return formatWeekdayDateDisplay(dateStr, { weekday: 'long' });
}

/** Query-string value for "do not split by band". */
const ALL_BANDS = 'all';

/**
 * How a single-grade selection is written into the same query param the bands use.
 *
 * One row of tabs, two kinds of choice: the two dismissal lines, then the seven grades
 * within them. Prefixing the grade values keeps them in one param — so one piece of state
 * drives the row and the two kinds can never both be active — while making them
 * impossible to confuse with a band id at a glance or in a URL.
 */
const GRADE_TAB_PREFIX = 'grade:';
type GradeTabValue = `${typeof GRADE_TAB_PREFIX}${string}`;

const gradeTabValue = (grade: string): GradeTabValue => `${GRADE_TAB_PREFIX}${grade}`;

/** The grade a tab value names, or null when it names a band. */
function gradeFromTabValue(value: string): string | null {
  return value.startsWith(GRADE_TAB_PREFIX) ? value.slice(GRADE_TAB_PREFIX.length) : null;
}

/**
 * The grade key a roster entry belongs to — normalized, so a child entered as
 * "Incoming TK" lands on the TK tab rather than opening a second one beside it.
 */
function gradeKeyOf(entry: RosterEntry): string {
  return normalizeGrade(entry.grade) ?? UNKNOWN_GRADE;
}

/** `TK`, `K`, `G3` — the short form a tab has room for. */
function gradeTabLabel(gradeKey: string): string {
  return gradeKey === UNKNOWN_GRADE ? 'No grade' : virtualGradeLabel(gradeKey);
}

/**
 * Students matching what the teacher typed, across the whole group.
 *
 * Deliberately searches every band and every school rather than the visible tab. At
 * check-out a parent gives a name and nothing else — the teacher does not know which
 * school's line the child is in, which is the whole reason the tabs are slow to work
 * through. Matching on `sortName` as well as `studentName` means "chen" finds a Chen
 * whether the teacher types the given name or the family name first.
 */
function matchEntries(entries: readonly RosterEntry[], term: string): RosterEntry[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return [];
  return entries
    .filter(e => `${e.studentName} ${e.sortName}`.toLowerCase().includes(needle))
    .sort(compareByFirstName);
}

/** The bands present in a roster, in dismissal order. */
function bandsIn(entries: readonly RosterEntry[]): GradeBand[] {
  const present = new Set(entries.map(e => gradeBand(e.grade?.trim() || UNKNOWN_GRADE)));
  return GRADE_BAND_ORDER.filter(band => present.has(band));
}

/**
 * The grades present in a roster, in dismissal order.
 *
 * Only grades that actually have a child today, the same way `bandsIn` works: a tab that
 * opens an empty list is a tab worth not drawing.
 */
function gradesIn(entries: readonly RosterEntry[]): string[] {
  const present = new Set(entries.map(gradeKeyOf));
  return [...present].sort((a, b) => gradeRank(a) - gradeRank(b));
}

export function StaffRoster({ staffUser }: Props) {
  const { programId: groupId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [checkOutTarget, setCheckOutTarget] = useState<{ recordId: string; studentId: string; studentName: string; photoPath: string | null } | null>(null);
  const [absentTarget, setAbsentTarget] = useState<{ recordId: string; studentName: string; previousStatus: AttendanceStatus } | null>(null);
  // Only one swipe tray is open at a time, so the parent owns which row that is.
  const [openRow, setOpenRow] = useState<{ enrollmentId: string; side: OpenSide }>({ enrollmentId: '', side: null });
  // The enlarged headshot. Opened from a roster row before check-in and from the check-out
  // sheet at handover, so it holds its own subject rather than reading either target.
  const [photoTarget, setPhotoTarget] = useState<{ name: string; url: string; subtitle: string | null } | null>(null);
  // Flex rows the teacher has deliberately unfrozen for today. An unscheduled child is
  // read-only by default so nobody records attendance against a day the family never
  // bought, but a child who turns up anyway still has to be recordable — so the freeze
  // lifts on an explicit press rather than being absolute. Resets on reload, which is
  // what we want: the override is for one child on one day, not a setting.
  const [unfrozenRows, setUnfrozenRows] = useState<ReadonlySet<string>>(new Set());
  // Deliberately not in the URL, unlike band and school: a search is what the teacher is
  // doing for the next fifteen seconds, not a place they would want to link someone to.
  const [search, setSearch] = useState('');
  // Ending a session cannot be undone from this screen — nothing walks a `picked_up` row
  // back — so the whole-class version asks once. Check-in-all needs no such step: it is
  // recoverable one row at a time.
  const [confirmCheckOutAll, setConfirmCheckOutAll] = useState(false);

  // Only programs that meet on the session day are addressable. That day is today — and
  // every action on this screen is live — unless nothing meets today, in which case it is
  // the next session and the whole screen is a read-only preview (`isPreview`).
  const {
    data: todaysPrograms = [],
    isLoading: programsLoading,
    today,
    sessionDate,
    isPreview,
    isSample,
  } = useTodaysPrograms(staffUser);

  // Only fetched for a teacher who actually holds a virtual class; everyone else pays
  // nothing for it.
  const holdsVirtual = (staffUser.virtual_class_keys?.length ?? 0) > 0;
  const { data: afterSchoolSeats = [] } = useAfterSchoolProgramsOn(holdsVirtual, sessionDate);
  const virtualKeys = staffUser.virtual_class_keys ?? null;

  const groups = useMemo(
    () => buildRosterGroups(todaysPrograms, virtualKeys, afterSchoolSeats),
    [todaysPrograms, virtualKeys, afterSchoolSeats],
  );
  const group = useMemo(
    () => resolveGroup(groupId, todaysPrograms, virtualKeys, afterSchoolSeats),
    [groupId, todaysPrograms, virtualKeys, afterSchoolSeats],
  );
  const pillarGroups = useMemo(() => groupsInSamePillar(groups, group), [groups, group]);
  const groupPrograms = useMemo(() => group?.programs ?? [], [group]);
  const groupProgramIds = useMemo(() => groupPrograms.map(p => p.id), [groupPrograms]);

  // How this group's students leave.
  //
  // The virtual check comes FIRST and is not reorderable. A virtual group's `programs` are
  // the after-school programs whose seats it borrows, so `attendanceModeFor` would read it
  // as a pickup line and ask the teacher to name the adult who collected a child from
  // Chinese — a handover that did not happen. Otherwise every program in a group shares a
  // type, so the first one decides for the whole tab.
  const mode: AttendanceMode = group?.virtualSubject
    ? 'class'
    : attendanceModeFor(groupPrograms[0]?.type);

  // A class gets one flat list of names, with none of the school / grade / room hierarchy.
  //
  // That hierarchy exists to work a hundred-child dismissal line in the order it queues up.
  // A chess class is a dozen children in one room whose teacher knows them all, so the same
  // structure becomes three levels of navigation over a list that already fits on a screen —
  // and the room it names is the child's homeroom at their day school, which says nothing
  // about where chess meets. Camp keeps the grouped view: it dismisses like after-school.
  const isFlatRoster = mode === 'class';

  const subject = group?.virtualSubject ?? null;
  const isVirtual = subject !== null;


  /**
   * Which band of the archive this roster belongs to.
   *
   * Camps get their own band there, which `pillarOfProgramType` does not have — it folds
   * them into Enrichment, correctly for a menu built from today's rosters and wrongly for
   * an archive where camps are most of the year's rows. So the type is read directly.
   */
  const historyBand: HistoryBand = subject
    ? subject.id
    : group?.isAfterSchool
      ? 'afterschool'
      : groupPrograms[0]?.type === 'camp'
        ? 'camp'
        : 'enrichment';

  /**
   * The class window — `2:30 PM – 3:15 PM` — shown beside the date in the header.
   *
   * Enrichment only, and that restriction is the point. An enrichment class is one program
   * with one start time and a `duration` its families were quoted, so the window is simply
   * true. The other three bands have no such time to state: the after-school tab merges
   * four programs that start at four different hours, a Chinese or Math tab borrows those
   * same after-school rows as seats (so their cron is the pickup line's, not the class's),
   * and camp durations on production are placeholder hours rather than the day the camp
   * actually runs. A time a teacher cannot trust is worse on the screen than absent.
   */
  const sessionTimeRange = useMemo(() => {
    if (historyBand !== 'enrichment') return null;
    const program = groupPrograms[0];
    return program ? formatSessionTimeRange(program.schedule_cron, program.duration) : null;
  }, [historyBand, groupPrograms]);

  /**
   * Where this roster meets, when every program in it meets in the same place. A merged
   * after-school tab whose programs use different rooms shows none rather than one of them,
   * and a virtual group's programs are its after-school seats — their room is not the class's.
   */
  const groupLocation = useMemo(() => {
    if (isVirtual) return null;
    const names = new Set(groupPrograms.map(p => programLocationName(p)));
    if (names.size !== 1) return null;
    return [...names][0];
  }, [isVirtual, groupPrograms]);

  // The after-school / enrichment roster. Deliberately handed an empty program list for a
  // virtual group: `groupPrograms` there is the after-school seats, and asking for their
  // rosters would pull in every after-school child rather than one grade.
  const { data: programEntries = [], isLoading: programRosterLoading } = useStaffRosterEntries(
    isVirtual ? [] : groupPrograms,
    sessionDate,
  );

  // Grade bands, but only where they mean something: the merged after-school group is a
  // pickup line that splits TK&K / G1-G5, whereas an enrichment class is one class with
  // one teacher however many grades sit in it.
  const bands = useMemo(
    () => (group?.isAfterSchool ? bandsIn(programEntries) : []),
    [group?.isAfterSchool, programEntries],
  );

  // One sub-tab row serving two different questions, because a screen only has room for
  // one. After school it is which dismissal line you are working; in Chinese or Math it is
  // which grade — and there the values come from the teacher's own scope rather than from
  // the roster, which is what lets the class be chosen *before* its roster is fetched.
  //
  // `all` is both the fallback when a group has no bands and a legitimate hand-typed value:
  // it merges the lines back into one list. A virtual group has no such merge — the grades
  // are separate classes with separate attendance, not two halves of one queue.
  /**
   * The single grades offered beside the bands, on the after-school pickup roster only.
   *
   * Teachers asked for this: at dismissal the grade is what they are working to, and
   * narrowing to "G3" is a question the band tabs could only answer by scrolling a mixed
   * list. Enrichment and camp rosters get nothing here — they are one class in one room,
   * where a grade filter has nothing to separate.
   */
  const gradeTabs = useMemo(
    () => (group?.isAfterSchool ? gradesIn(programEntries) : []),
    [group?.isAfterSchool, programEntries],
  );

  const subTabValues = useMemo(
    () =>
      isVirtual
        ? group!.virtualGrades
        : [...bands, ...gradeTabs.map(gradeTabValue), ALL_BANDS],
    [isVirtual, group, bands, gradeTabs],
  ) as readonly (GradeBand | VirtualGrade | GradeTabValue | typeof ALL_BANDS)[];

  const [subTab, setSubTab] = useUrlTabState<GradeBand | VirtualGrade | GradeTabValue | typeof ALL_BANDS>(
    'band',
    subTabValues,
    (isVirtual ? group!.virtualGrades[0] : bands[0]) ?? ALL_BANDS,
  );

  /** The single grade being worked, or null when the selection is a band. */
  const activeGradeFilter = isVirtual ? null : gradeFromTabValue(subTab);
  const activeBand =
    isVirtual || activeGradeFilter !== null || subTab === ALL_BANDS
      ? null
      : (subTab as GradeBand);
  const activeGrade = isVirtual ? (subTab as VirtualGrade) : null;

  /** `'chinese:3'` — what this tab's attendance is keyed by. Null outside a virtual group. */
  const classKey = useMemo(
    () => (subject && activeGrade ? virtualClassKey(subject.id, activeGrade) : null),
    [subject, activeGrade],
  );

  // One grade at a time, not all seven. `getRecord` below keys on enrollmentId alone, which
  // is only unambiguous while a single class is in flight — and a narrow query is the right
  // shape here anyway.
  const { data: virtualEntries = [], isLoading: virtualRosterLoading } = useVirtualClassRoster(
    classKey,
    groupPrograms,
    sessionDate,
  );

  const rosterEntries = isVirtual ? virtualEntries : programEntries;
  const rosterLoading = isVirtual ? virtualRosterLoading : programRosterLoading;

  const { data: records = [], isLoading: recordsLoading } = useAttendanceByPrograms(
    groupProgramIds,
    sessionDate,
    classKey ?? REAL_SESSION_KEY,
  );

  // Signed for the whole group's roster rather than the visible band, so switching bands or
  // schools does not re-sign and re-fetch faces the teacher has already looked at.
  const photoPaths = useMemo(() => rosterEntries.map(e => e.photoPath), [rosterEntries]);
  const { data: photoUrls = {} } = useStudentPhotoUrls(photoPaths);

  const upsertRoster = useUpsertRoster();
  const teacherCheckIn = useTeacherCheckIn();
  const markAbsent = useMarkAbsent();
  const clearAbsence = useClearAbsence();
  const markPickedUp = useMarkPickedUp();
  const undoParentCheckOutMutation = useUndoParentCheckOut();
  const bulkCheckIn = useBulkCheckIn();
  const bulkCheckOut = useBulkClassCheckOut();

  const bandEntries = useMemo(() => {
    // A single grade wins over the band it sits in — the two are one selection, and this
    // branch is the whole point of the grade tabs.
    if (activeGradeFilter !== null) {
      return rosterEntries.filter(e => gradeKeyOf(e) === activeGradeFilter);
    }
    return activeBand === null
      ? rosterEntries
      : rosterEntries.filter(e => gradeBand(e.grade?.trim() || UNKNOWN_GRADE) === activeBand);
  }, [rosterEntries, activeBand, activeGradeFilter]);

  // School → grade sections, matching the order students actually queue up in. Empty for a
  // flat roster, which collapses every tab and header below it.
  const schoolGroups = useMemo(
    () => (isFlatRoster ? [] : groupBySchoolThenGrade(bandEntries)),
    [bandEntries, isFlatRoster],
  );

  /** The whole roster as one list, in the same first-name order as every other view. */
  const flatEntries = useMemo(() => [...bandEntries].sort(compareByFirstName), [bandEntries]);
  const schoolNames = useMemo(() => schoolGroups.map(g => g.school), [schoolGroups]);
  // One school at a time: a teacher works a single school's line, so the others are just
  // noise to scroll past. Grades stay visible together inside the chosen school. Switching
  // band needs no reset — a school that is not in the new band simply falls back to its
  // first one.
  const [activeSchoolName, setActiveSchoolName] = useUrlTabState(
    'school',
    schoolNames,
    schoolNames[0] ?? '',
  );
  const activeSchool = schoolGroups.find(g => g.school === activeSchoolName) ?? schoolGroups[0] ?? null;

  const schoolLabels = useMemo(
    () => shortSchoolLabels(schoolGroups.map(g => g.school)),
    [schoolGroups],
  );

  /**
   * The blocks the roster body is made of, and what heads each one.
   *
   * The hierarchy inverts with the selection, because the useful heading is whichever of
   * school and grade is still varying. Working a band, the school is fixed by its tab and
   * each block is a grade. Working a single grade, the grade is fixed and the school is
   * what the teacher needs told — so the blocks become schools, all of them, and the
   * school tabs go away rather than narrowing a list that is already narrow.
   *
   * A school is normally one block. It splits only if its children's grades are spelled
   * two ways on file ("TK" and "Incoming TK"), which the tabs fold together but
   * `groupBySchoolThenGrade` does not; the label then names both so the split is legible
   * rather than looking like a duplicate.
   */
  const sections = useMemo(() => {
    if (isFlatRoster) return [];

    if (activeGradeFilter !== null) {
      return schoolGroups.flatMap(schoolGroup =>
        schoolGroup.grades.map(gradeGroup => ({
          key: `${schoolGroup.school}::${gradeGroup.grade}`,
          label:
            schoolGroup.grades.length > 1
              ? `${schoolGroup.school} · ${formatGradeLabel(gradeGroup.grade)}`
              : schoolGroup.school,
          count: gradeGroup.students.length,
          rooms: gradeGroup.rooms,
          hasRooms: gradeGroup.hasRooms,
          grade: gradeGroup.grade,
          school: schoolGroup.school,
        })),
      );
    }

    return (activeSchool?.grades ?? []).map(gradeGroup => ({
      key: gradeGroup.grade,
      label: formatGradeLabel(gradeGroup.grade),
      count: gradeGroup.students.length,
      rooms: gradeGroup.rooms,
      hasRooms: gradeGroup.hasRooms,
      grade: gradeGroup.grade,
      school: activeSchool?.school ?? '',
    }));
  }, [isFlatRoster, activeGradeFilter, schoolGroups, activeSchool]);

  // A program badge only earns its place when the group merges several: on a single-program
  // tab it would be the same word on every row.
  const showProgramBadge = groupPrograms.length > 1;

  // Where a child goes when the hour ends — only a question on an enrichment roster.
  const showAfterSchoolBadge = isEnrichmentRoster(mode, isVirtual);

  // How many students at each school are still waiting to be dealt with. This is what the
  // teacher is actually tracking during pickup — a total never changes, so it says nothing
  // about whether that school's line is done. A Flex student who did not pick today is
  // excluded: nobody is waiting for them, and counting them would leave a line that is
  // genuinely finished looking permanently outstanding.
  const remainingBySchool = useMemo(() => {
    const statusByEnrollment = new Map(records.map(r => [r.enrollmentId, r.status]));
    const remaining = new Map<string, number>();
    for (const group of schoolGroups) {
      let count = 0;
      for (const grade of group.grades) {
        for (const student of grade.students) {
          if (flexDayStatus(student, sessionDate) === 'not_scheduled') continue;
          if ((statusByEnrollment.get(student.enrollmentId) ?? 'not_arrived') === 'not_arrived') count++;
        }
      }
      remaining.set(group.school, count);
    }
    return remaining;
  }, [schoolGroups, records, sessionDate]);

  // Upsert 'not_arrived' records once the roster is ready (idempotent). Every program in
  // this group meets today, so this only ever writes rows for a real session. Unscheduled
  // Flex students are seeded too: they are marked on screen, not removed, because a child
  // who turns up anyway still has to be checkable in.
  //
  // A virtual class seeds against the child's after-school `programId` and `enrollmentId`
  // — the seat that puts them in the room — tagged with this tab's class key. That triple
  // plus the key is exactly the table's unique constraint, so re-opening a roster is a
  // no-op and Chinese, Math and after-school each keep their own row for the day.
  //
  // Never in preview. The day has not happened, and a seeded `not_arrived` row for it would
  // be a record of attendance nobody has taken yet — the pickup board would count it, and
  // an unenroll before the day would leave it behind.
  const rosterFingerprint = rosterEntries.map(e => e.enrollmentId).join(',');
  useEffect(() => {
    if (isPreview || rosterEntries.length === 0) return;
    void upsertRoster.mutate(
      rosterEntries.map(e => ({
        studentId: e.studentId,
        programId: e.programId,
        enrollmentId: e.enrollmentId,
        sessionDate,
        virtualClassKey: classKey ?? REAL_SESSION_KEY,
      })),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterFingerprint, sessionDate, classKey, isPreview]);

  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({ queryKey: ['staff-todays-programs'] });
    };
  }, [queryClient]);

  const isLoading = programsLoading || rosterLoading || recordsLoading;

  /** "October" — which month's Flex plan a preview is reading. */
  const previewMonthLabel = useMemo(() => {
    const [y, m] = sessionDate.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long' });
  }, [sessionDate]);

  function getRecord(enrollmentId: string) {
    return records.find(r => r.enrollmentId === enrollmentId);
  }

  // Counted over the roster on screen, not over every record for the day: a seeded row for
  // a child who is not expected today would otherwise pad the totals.
  const onRoster = new Set(rosterEntries.map(e => e.enrollmentId));
  const shownRecords = records.filter(r => onRoster.has(r.enrollmentId));
  const checkedInCount = shownRecords.filter(r => r.status === 'checked_in').length;
  const checkedOutCount = shownRecords.filter(r => r.status === 'picked_up').length;
  const unscheduledCount = rosterEntries.filter(
    e => flexDayStatus(e, sessionDate) === 'not_scheduled',
  ).length;

  // Whole-class shortcuts. A class of twenty ending together is one event to the teacher,
  // and pressing twenty rows to say so is the single most repetitive thing this screen
  // asks of them.
  //
  // Built from the roster on screen rather than from `records`, so a frozen Flex child —
  // one the plan says is not here today — is never swept up by a bulk press. The
  // individual row, after an explicit unfreeze, stays the only way to record them.
  const bulkEligible = (want: AttendanceStatus) =>
    rosterEntries
      .filter(e => !isFrozen(e) && (getRecord(e.enrollmentId)?.status ?? 'not_arrived') === want)
      .map(e => getRecord(e.enrollmentId)?.id)
      .filter((id): id is string => Boolean(id));

  const toCheckIn = mode === 'class' && !isPreview ? bulkEligible('not_arrived') : [];
  const toCheckOut = mode === 'class' && !isPreview ? bulkEligible('checked_in') : [];

  function runBulkCheckIn() {
    bulkCheckIn.mutate({ recordIds: toCheckIn, sessionDate, teacherName: staffUser.name });
  }

  function runBulkCheckOut() {
    setConfirmCheckOutAll(false);
    bulkCheckOut.mutate({ recordIds: toCheckOut, sessionDate, teacherName: staffUser.name });
  }

  // Landing on a group that isn't running today (a stale link, or the day rolled over
  // while the tab sat open) hands the teacher whichever group IS running instead.
  if (!programsLoading && !group) {
    const fallback = pickDefaultGroupId(todaysPrograms, groups);
    if (fallback) return <Navigate to={`/staff-app/attendance/${fallback}`} replace />;
    return (
      <StaffLayout staffUser={staffUser}>
        <h1 className="text-lg font-bold text-gray-900 mb-1">Attendance</h1>
        <p className="text-sm text-gray-500 mb-4">{formatSessionDate(today)}</p>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">No programs meet today.</p>
        </div>
      </StaffLayout>
    );
  }

  // A bookmark to one after-school program resolves to the merged group; canonicalise the
  // URL so a refresh, or a link the teacher copies, lands in the same place.
  if (group && groupId !== group.id) {
    return <Navigate to={`/staff-app/attendance/${group.id}`} replace />;
  }

  /**
   * True when this row is frozen: a Flex student on a day their family did not buy, whose
   * freeze the teacher has not lifted. Nothing is actionable until they press through.
   */
  function isFrozen(entry: RosterEntry) {
    return flexDayStatus(entry, sessionDate) === 'not_scheduled'
      && !unfrozenRows.has(entry.enrollmentId);
  }

  /** Actions for one student, shared by the swipe trays and the desktop buttons. */
  function actionsFor(entry: RosterEntry, status: AttendanceStatus) {
    const record = getRecord(entry.enrollmentId);
    if (!record) return { swipeLeftActions: [], swipeRightActions: [] };
    // A preview is read-only: a record can exist for a future day (an absence a parent
    // reported ahead), but nothing is recorded against a session that has not started.
    if (isPreview) return { swipeLeftActions: [], swipeRightActions: [] };
    // Frozen rows offer nothing to swipe or press — the unfreeze control is the only way in.
    if (isFrozen(entry)) return { swipeLeftActions: [], swipeRightActions: [] };

    const checkIn: SwipeAction = {
      key: 'check-in',
      label: 'Check In',
      className: 'bg-green-600 text-white',
      onSelect: () => void teacherCheckIn.mutate({
        recordId: record.id,
        programId: entry.programId,
        sessionDate,
        teacherName: staffUser.name ?? staffUser.email,
        studentId: entry.studentId,
        previousStatus: status,
        previousAbsenceReason: record.absenceReason,
      }),
    };
    const absent: SwipeAction = {
      key: 'absent',
      label: 'Absent',
      className: 'bg-red-500 text-white',
      onSelect: () => setAbsentTarget({
        recordId: record.id,
        studentName: entry.studentName,
        previousStatus: status,
      }),
    };
    // The way back out of an absence. Deliberately not a check-in: a teacher who marked the
    // wrong child, or picked the wrong reason, is saying the absence was never true — not
    // that the child has since walked in.
    const undoAbsent: SwipeAction = {
      key: 'undo-absent',
      label: 'Undo Absent',
      className: 'bg-gray-600 text-white',
      onSelect: () => void clearAbsence.mutate({
        recordId: record.id,
        programId: entry.programId,
        sessionDate,
        studentId: entry.studentId,
        teacherName: staffUser.name ?? staffUser.email,
        previousReason: record.absenceReason,
      }),
    };
    // After school this opens the sheet that asks who is collecting the child. In a class it
    // records the session as finished on the spot — one tap, no sheet, nobody to name.
    const checkOut: SwipeAction = {
      key: 'check-out',
      label: 'Check Out',
      className: 'bg-blue-600 text-white',
      onSelect: () => {
        if (mode === 'class') {
          markPickedUp.mutate({
            mode: 'class',
            recordId: record.id,
            programId: entry.programId,
            sessionDate,
            recordedByName: staffUser.name ?? staffUser.email,
            studentId: entry.studentId,
            studentName: entry.studentName,
          });
          return;
        }
        setCheckOutTarget({
          recordId: record.id,
          studentId: entry.studentId,
          studentName: entry.studentName,
          photoPath: entry.photoPath,
        });
      },
    };

    // The way back from a parent's self check-out, and only that: a staff check-out stays
    // one-way. A parent's tap is not a handover anyone here witnessed, so a child it calls
    // gone may still be in the room.
    const undoParentCheckOut: SwipeAction = {
      key: 'undo-parent-check-out',
      label: 'Still Here',
      className: 'bg-gray-600 text-white',
      onSelect: () => void undoParentCheckOutMutation.mutate({
        recordId: record.id,
        programId: entry.programId,
        sessionDate,
        studentId: entry.studentId,
        studentName: entry.studentName,
        teacherName: staffUser.name ?? staffUser.email,
      }),
    };

    // Swipe left = set arrival state; swipe right = send them home.
    if (status === 'not_arrived') return { swipeLeftActions: [checkIn, absent], swipeRightActions: [] };
    if (status === 'absent') return { swipeLeftActions: [checkIn, undoAbsent], swipeRightActions: [] };
    if (status === 'checked_in') return { swipeLeftActions: [absent], swipeRightActions: [checkOut] };
    if (status === 'picked_up' && record.checkedOutBy === 'parent') {
      return { swipeLeftActions: [undoParentCheckOut], swipeRightActions: [] };
    }
    return { swipeLeftActions: [], swipeRightActions: [] };
  }

  /**
   * The buttons voice search should show beside a child it found.
   *
   * The same `actionsFor` the rows use, flattened: voice reaches a student, it does not
   * decide anything about them. So a check-out still opens the sheet that asks who collected
   * the child, an absence still asks for a reason, and a frozen Flex row still offers
   * nothing — one set of rules, whether the child was found by scrolling or by speaking.
   */
  function voiceActionsFor(entry: RosterEntry): SwipeAction[] {
    const status = getRecord(entry.enrollmentId)?.status ?? 'not_arrived';
    const { swipeLeftActions, swipeRightActions } = actionsFor(entry, status);
    return [...swipeLeftActions, ...swipeRightActions];
  }

  /**
   * One student, however they were reached — through the school/grade tabs or through the
   * search box. Search results carry `showContext`, because a flat list of names has none
   * of the school and grade that the grouped view puts in its headers, and "which line is
   * this child in?" is the next thing the teacher needs after finding them.
   */
  function renderStudentRow(entry: RosterEntry, showContext = false) {
    const record = getRecord(entry.enrollmentId);
    const status = record?.status ?? 'not_arrived';
    const { swipeLeftActions, swipeRightActions } = actionsFor(entry, status);
    const flex = flexDayStatus(entry, sessionDate);
    const frozen = isFrozen(entry);
    const isPerDay = entry.billingType === 'per_day';

    return (
      <SwipeableRow
        key={entry.enrollmentId}
        swipeLeftActions={swipeLeftActions}
        swipeRightActions={swipeRightActions}
        openSide={openRow.enrollmentId === entry.enrollmentId ? openRow.side : null}
        onOpenSideChange={side => setOpenRow({ enrollmentId: entry.enrollmentId, side })}
      >
        <div
          className={`flex items-start gap-3 px-3 py-2.5 ${
            flex === 'not_scheduled' ? 'bg-amber-50/40' : ''
          }`}
        >
          {/* The whole point of the feature: a substitute reading this row
              can see who they are about to release, not just a name. */}
          <StudentAvatar
            name={entry.studentName}
            photoUrl={entry.photoPath ? photoUrls[entry.photoPath] : null}
            size="sm"
            onEnlarge={() => {
              const url = entry.photoPath ? photoUrls[entry.photoPath] : null;
              if (!url) return;
              setPhotoTarget({
                name: entry.studentName,
                url,
                subtitle: [formatGradeLabel(entry.grade?.trim() || UNKNOWN_GRADE), entry.school]
                  .filter(Boolean)
                  .join(' · ') || null,
              });
            }}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="font-medium text-gray-900">{entry.studentName}</span>
              {showProgramBadge && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {shortProgramLabel(entry.programName)}
                </span>
              )}
              {entry.hasAllergies && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                  ⚠ {entry.allergiesDetails || 'Allergy'}
                </span>
              )}
              {entry.viaSummerPass && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  Pass
                </span>
              )}
              {showAfterSchoolBadge && <AfterSchoolBadge inAfterSchool={entry.inAfterSchool} />}
            </div>

            {showContext && (
              <p className="mt-0.5 text-[11px] text-gray-500">
                {[
                  entry.school,
                  formatGradeLabel(entry.grade?.trim() || UNKNOWN_GRADE),
                  entry.roomNumber ? `Room ${entry.roomNumber}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            )}

            {/* Flex: whether the family bought TODAY. Without this a
                teacher cannot tell a child nobody expects from one who
                simply has not arrived yet. */}
            {flex === 'not_scheduled' && (
              <div className="mt-1">
                <p className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
                  <CalendarOff className="h-3 w-3 flex-shrink-0" />
                  Not one of this student's Flex days
                </p>
                {/* The freeze is against recording attendance by reflex on a
                    day nobody bought — not against recording a child who is
                    standing there. One press lifts it for this row. */}
                {isPreview ? null : frozen ? (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setUnfrozenRows(prev => new Set(prev).add(entry.enrollmentId));
                    }}
                    className="mt-1 block text-[11px] font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                  >
                    Came in anyway — unlock
                  </button>
                ) : (
                  <p className="mt-1 text-[11px] text-amber-800">Unlocked for today</p>
                )}
              </div>
            )}
            {flex === 'unplanned' && (
              <p className="mt-1 text-[11px] text-gray-500">
                {isPreview
                  ? `Flex — no day plan on file for ${previewMonthLabel} yet`
                  : 'Flex — no day plan on file for this month'}
              </p>
            )}

            {isPerDay && (entry.selectedDays ?? []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {(entry.selectedDays ?? []).map(d => (
                  <span key={d} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100">
                    {shortDate(d)}
                  </span>
                ))}
              </div>
            )}
            <AttendanceRecordFacts record={record} />
          </div>

          {/* In preview nobody has been checked in or out yet, so "Not arrived" would be
              a claim about a day that has not happened. An absence reported ahead is real
              and keeps its badge. */}
          {isPreview && status === 'not_arrived' ? (
            <span className="flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
              Expected
            </span>
          ) : (
            <AttendanceStatusBadge status={status} />
          )}

          {/* Desktop keeps explicit buttons — there is no swipe on a mouse. */}
          <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 mt-0.5">
            {[...swipeLeftActions, ...swipeRightActions].map(action => (
              <button
                key={action.key}
                onClick={e => { e.stopPropagation(); action.onSelect(); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${action.className} hover:opacity-90`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </SwipeableRow>
    );
  }

  // The class on screen, handed to the archive as its opening selection.
  const historyParams = new URLSearchParams({
    band: historyBand,
    ...(isVirtual && activeGrade ? { grade: activeGrade } : {}),
  }).toString();

  // Searched across the whole group, not the visible band or school — see `matchEntries`.
  const searching = search.trim().length > 0;
  const searchResults = searching ? matchEntries(rosterEntries, search) : [];

  const absentTargetEntry = absentTarget
    ? rosterEntries.find(e => getRecord(e.enrollmentId)?.id === absentTarget.recordId)
    : undefined;
  const checkOutTargetEntry = checkOutTarget
    ? rosterEntries.find(e => getRecord(e.enrollmentId)?.id === checkOutTarget.recordId)
    : undefined;

  return (
    <StaffLayout staffUser={staffUser}>
      {isSample && (
        <>
          <SampleWatermark />
          <SampleModeBanner />
        </>
      )}

      {/* Nothing meets today, so this is the next session's roster. Said once, up top, in
          the words a teacher needs on a Saturday: who is coming, and that nothing here can
          be recorded until the day. */}
      {isPreview && (
        <div
          data-testid="preview-banner"
          className="mb-3 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
        >
          <CalendarClock className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Next session: {formatSessionDate(sessionDate)}.</span>{' '}
            Nothing meets today, so this is who is expected then. Read-only — check-in opens on
            the day.
          </p>
        </div>
      )}

      {/* Only this pillar's groups. The strip used to mix After School, Chinese, Math and
          every enrichment class into one row; the pillars now carry that first choice in
          the menu, so this is left doing the one job it is good at — choosing between the
          classes of the category you are already in. After School, Chinese and Math each
          hold exactly one group, so it disappears there entirely. */}
      <GroupSwitcher
        groups={pillarGroups}
        activeId={group?.id ?? ''}
        // Band and school are the previous group's vocabulary; carrying them into the next
        // one would select a tab that no longer exists.
        onSelect={id => navigate(`/staff-app/attendance/${id}`)}
      />

      <div className="mb-3 flex items-start gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">
            {/* "Chinese G3", not just "Chinese" — the subject alone names seven classes, and
                the heading is what a teacher checks before recording anything. */}
            {isVirtual && activeGrade ? `${group!.label} ${virtualGradeLabel(activeGrade)}` : group?.label}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500">
            {formatSessionDate(sessionDate)}
            {/* Teachers asked for the hour beside the day: standing outside a room at 5pm on
                a Friday, which of three chess classes is this one. */}
            {sessionTimeRange && (
              <span className="whitespace-nowrap"> · {sessionTimeRange}</span>
            )}
          </p>
          <LocationPill name={groupLocation} size="sm" className="mt-1" />
        </div>

        {/* Yesterday, one press away, already pointed at the class on screen. The archive
            has its own menu entry; this is the same door from where the question is
            actually asked — standing in front of the roster, about the class in hand. */}
        <Link
          to={`/staff-app/history?${historyParams}`}
          className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <History className="h-3.5 w-3.5" />
          History
        </Link>
      </div>

      {!isLoading && rosterEntries.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span className="font-medium text-gray-900">{rosterEntries.length} students</span>
          {isPreview ? (
            <span className="text-sky-700 font-medium">
              {rosterEntries.length - unscheduledCount} expected
            </span>
          ) : (
            <>
              <span className="text-green-700 font-medium">{checkedInCount} checked in</span>
              <span className="text-blue-600">{checkedOutCount} checked out</span>
            </>
          )}
          {unscheduledCount > 0 && (
            <span className="text-amber-700">
              {unscheduledCount} not scheduled {isPreview ? 'that day' : 'today'}
            </span>
          )}

          {/* Class-mode only, and that is the whole rule. A class ending is one event, so
              one press can assert it. A pickup line is twenty separate handovers to twenty
              named adults, and a button claiming all of them at once would be a lie —
              which is why there is no bulk check-out for after-school or camp. */}
          {mode === 'class' && !searching && (
            <span className="ml-auto flex items-center gap-2">
              {toCheckIn.length > 0 && (
                <button
                  type="button"
                  onClick={runBulkCheckIn}
                  disabled={bulkCheckIn.isPending}
                  className="px-2.5 py-1 rounded-lg border border-green-600 text-green-700 text-xs font-medium hover:bg-green-50 disabled:opacity-50"
                >
                  {bulkCheckIn.isPending ? 'Checking in…' : `Check in all ${toCheckIn.length}`}
                </button>
              )}

              {toCheckOut.length > 0 && (
                confirmCheckOutAll ? (
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-600">End session for {toCheckOut.length}?</span>
                    <button
                      type="button"
                      onClick={runBulkCheckOut}
                      disabled={bulkCheckOut.isPending}
                      className="px-2 py-1 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmCheckOutAll(false)}
                      className="px-2 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                    >
                      No
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCheckOutAll(true)}
                    className="px-2.5 py-1 rounded-lg border border-blue-600 text-blue-700 text-xs font-medium hover:bg-blue-50"
                  >
                    Check out all {toCheckOut.length}
                  </button>
                )
              )}
            </span>
          )}
        </div>
      )}

      {/* Grade sub-tabs for Chinese and Math. Rendered above the loading and empty states,
          unlike the after-school band tabs below: a grade with nobody in it is a perfectly
          ordinary result, and hiding the tabs there would strand the teacher on an empty
          screen with no way back to the grade they actually teach. */}
      {isVirtual && group!.virtualGrades.length > 1 && (
        <ScrollTabs
          label="grades"
          activeKey={activeGrade ?? undefined}
          className="-mx-4 sm:-mx-6 lg:-mx-10 mb-3 border-b border-gray-200"
        >
          {group!.virtualGrades.map(grade => {
            const isActive = grade === activeGrade;
            return (
              <button
                key={grade}
                type="button"
                data-tab-key={grade}
                data-grade={grade}
                onClick={() => setSubTab(grade)}
                aria-current={isActive ? 'page' : undefined}
                className={`flex-shrink-0 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {virtualGradeLabel(grade)}
              </button>
            );
          })}
        </ScrollTabs>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : rosterEntries.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">
            {isVirtual
              // Nobody is enrolled in a virtual class by definition, so the after-school
              // wording would send a teacher hunting for an enrollment that never exists.
              ? `No after-school students in this grade ${isPreview ? 'that day' : 'today'}.`
              : 'No students enrolled in this program.'}
          </p>
        </div>
      ) : (
        <>
          {/* Find a student by name, across every band and school in this group.
              At check-out a parent gives a name and the teacher — often a substitute —
              has to find the child without knowing which school's line they are in.
              Typing here replaces the tabs with a flat list of matches. */}
          <div className="relative mb-3">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              inputMode="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Find a student"
              aria-label="Find a student"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              /* 16px on phones: anything smaller makes iOS Safari zoom the page in on
                 focus, which leaves the teacher scrolled sideways mid-handover. */
              className="w-full pl-9 pr-10 py-2.5 text-base sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searching && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                /* A thumb-sized target of its own: the native `type="search"` clear
                   affordance is absent on Android and tiny where it does appear. */
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Grade-band sub-tabs — the TK&K line and the G1-G5 line dismiss separately.
              Hidden while searching: a search deliberately ignores them, so leaving them
              on screen would suggest the results were narrowed by the selected tab. */}
          {!searching && (bands.length > 1 || gradeTabs.length > 1) && (
            <ScrollTabs
              label="grade bands"
              activeKey={subTab}
              className="-mx-4 sm:-mx-6 lg:-mx-10 mb-3 border-b border-gray-200"
            >
              {bands.map(band => {
                const isActive = band === activeBand;
                const count = rosterEntries.filter(
                  e => gradeBand(e.grade?.trim() || UNKNOWN_GRADE) === band,
                ).length;
                return (
                  <button
                    key={band}
                    type="button"
                    data-tab-key={band}
                    data-band={band}
                    onClick={() => setSubTab(band)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex-shrink-0 flex items-baseline gap-1.5 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {GRADE_BAND_LABEL[band]}
                    <span className="text-xs text-gray-400">{count}</span>
                  </button>
                );
              })}

              {/* A rule, not a gap: the two dismissal lines and the seven grades are
                  different kinds of choice sharing one row, and without a visible break
                  "G1 – G5" and "G1" sit side by side reading as near-duplicates. */}
              {bands.length > 0 && gradeTabs.length > 0 && (
                <span aria-hidden="true" className="flex-shrink-0 self-center mx-1 h-5 w-px bg-gray-200" />
              )}

              {gradeTabs.map(gradeKey => {
                const value = gradeTabValue(gradeKey);
                const isActive = value === subTab;
                const count = rosterEntries.filter(e => gradeKeyOf(e) === gradeKey).length;
                return (
                  <button
                    key={value}
                    type="button"
                    data-tab-key={value}
                    data-grade-tab={gradeKey}
                    onClick={() => setSubTab(value)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex-shrink-0 flex items-baseline gap-1.5 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {gradeTabLabel(gradeKey)}
                    <span className="text-xs text-gray-400">{count}</span>
                  </button>
                );
              })}
            </ScrollTabs>
          )}

          {/* School sub-tabs — work one school's line at a time. Absent under a single-grade
              selection, where every school is shown at once and there is nothing to pick. */}
          {!searching && activeGradeFilter === null && schoolGroups.length > 1 && (
            <ScrollTabs
              label="schools"
              activeKey={activeSchool?.school}
              className="sticky top-14 sm:top-0 z-20 bg-white -mx-4 sm:-mx-6 lg:-mx-10 mb-3 border-b border-gray-200"
            >
              {schoolGroups.map(group => {
                const isActive = group.school === activeSchool?.school;
                return (
                  <button
                    key={group.school}
                    type="button"
                    data-tab-key={group.school}
                    data-school={group.school}
                    title={`${group.school} — ${group.total} students, ${remainingBySchool.get(group.school) ?? 0} not yet checked in`}
                    onClick={() => setActiveSchoolName(group.school)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex-shrink-0 flex items-baseline gap-1.5 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {schoolLabels.get(group.school) ?? group.school}
                    {(remainingBySchool.get(group.school) ?? 0) > 0 ? (
                      <span
                        className={`text-xs font-semibold px-1.5 rounded-full ${
                          isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {remainingBySchool.get(group.school)}
                      </span>
                    ) : (
                      <span className="text-xs text-green-600" aria-label="all students recorded">✓</span>
                    )}
                  </button>
                );
              })}
            </ScrollTabs>
          )}

          {/* The tab is abbreviated, so name the selected school in full here. */}
          {!searching && activeGradeFilter === null && activeSchool && schoolGroups.length > 1 && (
            <p className="mb-2 text-sm font-semibold text-gray-900">{activeSchool.school}</p>
          )}

          {searching ? (
            searchResults.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <p className="text-gray-500 text-sm">No student on {isPreview ? 'this' : "today's"} roster matches "{search.trim()}".</p>
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  Show the full roster
                </button>
              </div>
            ) : (
              <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <header className="flex items-baseline gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    {searchResults.length} match{searchResults.length === 1 ? '' : 'es'}
                  </h2>
                  <span className="text-xs text-gray-400">across every school and grade</span>
                </header>
                <div className="divide-y divide-gray-100">
                  {/* Context orients a name inside a hierarchy. A flat roster has none. */}
                  {searchResults.map(entry => renderStudentRow(entry, !isFlatRoster))}
                </div>
              </section>
            )
          ) : isFlatRoster ? (
            <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="divide-y divide-gray-100">
                {flatEntries.map(entry => renderStudentRow(entry))}
              </div>
            </section>
          ) : (
          /* Grades within the selected school — or, under a single-grade selection, one block
             per school. Either way each block is boxed so they read as distinct. */
          <div className="space-y-4">
            {sections.map(section => (
              <section
                key={section.key}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <header
                  data-grade={section.grade}
                  data-section-school={section.school}
                  className="flex items-baseline gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200"
                >
                  <h2 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                    {section.label}
                  </h2>
                  <span className="text-xs text-gray-400">{section.count}</span>
                </header>

                <div className="divide-y divide-gray-100">
                  {section.rooms.map(cluster => (
                    <div key={cluster.room ?? '__no_room__'}>
                      {/* A grade stays one block, but each room gets a banded header
                          (badge + headcount) so it's obvious the room covers every
                          student below it, not just the first. Suppressed entirely
                          when nobody in the grade has a room on file. */}
                      {section.hasRooms && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50/80 border-y border-gray-100">
                          {cluster.room ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 whitespace-nowrap">
                              Room {cluster.room}
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-400 whitespace-nowrap">
                              No room
                            </span>
                          )}
                          <span className="text-[11px] text-gray-400">
                            {cluster.students.length} student{cluster.students.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      )}
                      <div className="divide-y divide-gray-100">
                        {cluster.students.map(entry => renderStudentRow(entry))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
          )}
        </>
      )}

      {/* Voice sits over the roster rather than inside it: a teacher working a pickup line
          has a parent in front of them, and the fastest way to one name among a hundred is
          to say it. Only once there is a roster to say a name against. */}
      {!isLoading && rosterEntries.length > 0 && !isPreview && (
        <VoiceAttendance
          entries={rosterEntries}
          statusFor={entry => getRecord(entry.enrollmentId)?.status ?? 'not_arrived'}
          actionsFor={voiceActionsFor}
          photoUrlFor={entry => (entry.photoPath ? photoUrls[entry.photoPath] ?? null : null)}
          staffEmail={staffUser.email}
          band={classKey ?? activeBand ?? subTab ?? null}
          showAfterSchoolBadge={showAfterSchoolBadge}
        />
      )}

      {absentTarget && absentTargetEntry && (
        <AbsenceReasonModal
          studentName={absentTarget.studentName}
          isCorrection={absentTarget.previousStatus === 'checked_in'}
          isSaving={markAbsent.isPending}
          onClose={() => setAbsentTarget(null)}
          onConfirm={reason => {
            markAbsent.mutate({
              recordId: absentTarget.recordId,
              programId: absentTargetEntry.programId,
              sessionDate,
              reason,
              studentId: absentTargetEntry.studentId,
              teacherName: staffUser.name ?? staffUser.email,
              previousStatus: absentTarget.previousStatus,
            });
            setAbsentTarget(null);
          }}
        />
      )}

      {checkOutTarget && checkOutTargetEntry && (
        <CheckOutModal
          recordId={checkOutTarget.recordId}
          studentName={checkOutTarget.studentName}
          studentId={checkOutTarget.studentId}
          photoUrl={checkOutTarget.photoPath ? photoUrls[checkOutTarget.photoPath] : null}
          onEnlargePhoto={photo => setPhotoTarget({
            name: photo.name,
            url: photo.url,
            subtitle: photo.subtitle ?? null,
          })}
          programId={checkOutTargetEntry.programId}
          sessionDate={sessionDate}
          recordedByName={staffUser.name ?? staffUser.email}
          onClose={() => setCheckOutTarget(null)}
        />
      )}

      {photoTarget && (
        <StudentPhotoLightbox
          name={photoTarget.name}
          photoUrl={photoTarget.url}
          subtitle={photoTarget.subtitle}
          onClose={() => setPhotoTarget(null)}
        />
      )}
    </StaffLayout>
  );
}
